import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Paperclip, Upload, Trash2, File as FileIcon } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

/** Reusable attachments block. Renders list + upload button for any doc. */
export default function Attachments({ docType, docId, compact = false }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!docId) return;
    try {
      const r = await api.get(`/attachments/${docType}/${docId}`);
      setItems(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, [docType, docId]);

  const onPick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/attachments/${docType}/${docId}`, fd);
      toast.success("Uploaded"); e.target.value = ""; load();
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Upload failed"); }
    finally { setBusy(false); }
  };

  const download = async (att) => {
    try {
      const res = await api.get(`/attachments/download/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = att.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
  };

  const remove = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"?`)) return;
    try { await api.delete(`/attachments/${att.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!docId) return (
    <div className="text-[10px] font-mono uppercase text-muted-foreground py-2">
      Save the document first to attach files
    </div>
  );

  return (
    <div className={compact ? "space-y-2" : "border rounded-sm p-3 space-y-2"} data-testid={`attachments-${docType}-${docId}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attachments ({items.length})</div>
        <label className="cursor-pointer">
          <input type="file" onChange={onPick} className="hidden" data-testid={`attach-input-${docType}`} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" />
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded-sm hover:bg-muted">
            <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload"}
          </span>
        </label>
      </div>
      <div className="space-y-1">
        {items.length === 0 && <div className="text-xs text-muted-foreground italic py-1">No files attached</div>}
        {items.map((a) => (
          <div key={a.id} className="flex items-center gap-2 text-xs border rounded-sm px-2 py-1.5 hover:bg-muted/50">
            <FileIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <button onClick={() => download(a)} className="flex-1 text-left truncate hover:text-accent" data-testid={`attach-file-${a.id}`}>{a.filename}</button>
            <span className="text-[10px] font-mono text-muted-foreground shrink-0">{Math.ceil(a.size / 1024)} KB</span>
            <span className="text-[10px] text-muted-foreground shrink-0">{fmtDate(a.uploaded_at?.slice(0, 10))}</span>
            <button onClick={() => remove(a)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`attach-del-${a.id}`}><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
